import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuestionnaire } from '../../lib/QuestionnaireContext';
import { evaluateConditionalRules } from '../../lib/templateUtils';

import QuestionnaireSidebar from './QuestionnaireSidebar';
import logo from '../../logo.svg';

const QuestionnaireLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentQuestionnaire, activeTemplate, loadActiveTemplate } = useQuestionnaire();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Load the active template when component mounts
  useEffect(() => {
    if (!activeTemplate) {
      loadActiveTemplate(true); // Force fresh load
    }
  }, [activeTemplate, loadActiveTemplate]);
  
  // Convert template-based steps to sidebar format using the same logic as the form
  const steps = useMemo(() => {
    if (!activeTemplate) {
      // Return empty array while template is loading
      return [];
    }
    
    // Get form data for conditional logic evaluation
    const formData = currentQuestionnaire?.formData || {};
    
    // Now we need to map these steps back to their original sections/subsections
    // to properly display the hierarchy in the sidebar
    const sidebarSections: any[] = [];
    
    // Sort template sections by order
    const sortedSections = [...activeTemplate.sections].sort((a, b) => a.order - b.order);
    
    let stepIndex = 0;
    
    sortedSections.forEach(section => {
      // Check if this section should be visible based on conditional rules
      const isSectionVisible = !section.conditionalRules || section.conditionalRules.length === 0 || 
        evaluateConditionalRules(section.conditionalRules, formData);
      
      if (!isSectionVisible) {
        return; // Skip this section
      }
      
      if (section.subsections && section.subsections.length > 0) {
        // Filter visible subsections
        const visibleSubsections = section.subsections.filter(subsection => {
          return !subsection.conditionalRules || subsection.conditionalRules.length === 0 ||
            evaluateConditionalRules(subsection.conditionalRules, formData);
        });
        
        if (visibleSubsections.length > 0) {
          if (visibleSubsections.length === 1) {
            // Section with only one subsection - create a direct section (no subsections in sidebar)
            sidebarSections.push({
              title: section.title,
              description: section.description || '',
              isSection: true,
              stepIndex: stepIndex,
              subSteps: []
            });
            stepIndex++;
          } else {
            // Section with multiple subsections - create section with subsections
            const subSteps = visibleSubsections.map((subsection, subIndex) => ({
              title: subsection.title,
              description: subsection.description || '',
              stepIndex: stepIndex + subIndex
            }));
            
            sidebarSections.push({
              title: section.title,
              description: section.description || '',
              isSection: true,
              subSteps: subSteps
            });
            stepIndex += visibleSubsections.length;
          }
        }
      }
    });
    
    return sidebarSections;
  }, [activeTemplate, currentQuestionnaire?.formData]);
  
  // Determine the current step based on the location and search params
  const getCurrentStep = () => {
    if (location.pathname === '/questionnaire') {
      return -1; // Dashboard
    } else if (location.pathname === '/questionnaire/success') {
      // Return a high number to indicate completion
      return 100; // Success page (after all steps)
    } else if (location.pathname.includes('/questionnaire/form')) {
      // Check if there's a step parameter in the URL
      const stepParam = searchParams.get('step');
      if (stepParam !== null) {
        const stepNumber = parseInt(stepParam, 10);
        console.log(`Step param from URL: ${stepParam}, parsed as: ${stepNumber}`);
        return stepNumber;
      } else if (currentQuestionnaire) {
        console.log(`No step param, using currentQuestionnaire.currentStep: ${currentQuestionnaire.currentStep || 0}`);
        return currentQuestionnaire.currentStep || 0;
      }
    }
    console.log('Default to first step');
    return 0; // Default to first step
  };

  // Add a class to the body to ensure full height layout
  useEffect(() => {
    document.body.classList.add('h-full');
    document.documentElement.classList.add('h-full');
    
    return () => {
      document.body.classList.remove('h-full');
      document.documentElement.classList.remove('h-full');
    };
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if we're clicking outside the sidebar and not on the hamburger button
      if (sidebarOpen && 
          !target.closest('.sidebar') && 
          !target.closest('.hamburger-button')) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen h-screen flex bg-background overflow-hidden">
      {/* Mobile header with logo and menu button */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between p-4 bg-white border-b border-gray-200 md:hidden">
        <button 
          className="hamburger-button p-2 rounded-md bg-primary text-primary-foreground"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            {sidebarOpen ? (
              <path d="M18 6L6 18M6 6l12 12" />
            ) : (
              <path d="M3 12h18M3 6h18M3 18h18" />
            )}
          </svg>
        </button>
        
        {/* Mobile logo - centered in header */}
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <a href="/questionnaire" onClick={(e) => {
            e.preventDefault();
            navigate('/questionnaire');
          }}>
            <img src={logo} alt="Logo" className="h-[3.75rem] w-auto cursor-pointer" />
          </a>
        </div>
        
        {/* Empty div to balance the layout */}
        <div className="w-10"></div>
      </div>

      {/* Sidebar - hidden on mobile by default, shown when sidebarOpen is true */}
      <div className={`sidebar ${sidebarOpen ? 'block' : 'hidden'} md:block md:static fixed inset-0 z-40`}>
        <QuestionnaireSidebar 
          steps={steps} 
          currentStep={getCurrentStep()} 
          onCloseMobile={() => setSidebarOpen(false)} 
        />
      </div>

      {/* Main content - full width on mobile */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
};

export default QuestionnaireLayout; 